import { useCallback, useState } from 'react';
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { archiveCase } from '../../../../services/patientFiles';
import { apiErrorMessage } from '../../../../utils/apiError';
import { ReasonField } from '../../../work-queue/tasks/approval-review/ReasonField';
import type { ActionRunnerProps } from '../types';

// Mirrors the backend archiveCaseBody validator (terminal.validator.ts): reason required, up to 5000 chars.
// ReasonField's minLength prop needs a number even though this reason has no real floor.
const REQUIRED_REASON_LENGTH = 1;
const MAX_REASON_LENGTH = 5000;

type Step = 'confirm' | 'success' | 'error';

function ArchiveCaseAction({ patientId, patientName, onBack, onDataChange }: ActionRunnerProps) {
    const [step, setStep] = useState<Step>('confirm');
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const trimmedReason = reason.trim();
    const canSubmit =
        trimmedReason.length > 0 && trimmedReason.length <= MAX_REASON_LENGTH && !isSubmitting;

    const handleSubmit = useCallback(async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            await archiveCase(patientId, trimmedReason);
            onDataChange?.();
            setStep('success');
        } catch (err: unknown) {
            setErrorMessage(apiErrorMessage(err, 'Failed to archive case. Please try again.'));
            setStep('error');
        } finally {
            setIsSubmitting(false);
        }
    }, [canSubmit, patientId, trimmedReason, onDataChange]);

    const handleSuccessBack = useCallback(() => {
        onBack();
    }, [onBack]);

    const handleRetry = useCallback(() => {
        setStep('confirm');
        setErrorMessage(null);
    }, []);

    if (step === 'success') {
        return (
            <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                    <div className="w-[120px]" />
                    <div className="text-sm font-semibold text-gray-800">Archive case</div>
                    <div className="w-[120px]" />
                </div>
                <div className="flex-1 min-h-0 overflow-auto p-6">
                    <div className="max-w-xl mx-auto">
                        <div className="flex items-start gap-3 p-4 rounded-xl border border-green-200 bg-green-50">
                            <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-green-800">
                                <div className="font-semibold mb-1">
                                    Case archived successfully.
                                </div>
                                <p>
                                    <span className="font-medium">{patientName}</span>&apos;s case
                                    has been archived and removed from the active work queue. ODO
                                    staff can reactivate it from the case detail panel, and the
                                    practice can reactivate it from their dashboard.
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={handleSuccessBack}
                                className="bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-800 transition-colors"
                            >
                                Back to actions
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'error') {
        return (
            <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                    >
                        <ArrowLeftIcon className="w-3.5 h-3.5" />
                        Back to actions
                    </button>
                    <div className="text-sm font-semibold text-gray-800">Archive case</div>
                    <div className="w-[120px]" />
                </div>
                <div className="flex-1 min-h-0 overflow-auto p-6">
                    <div className="max-w-xl mx-auto">
                        <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50">
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-red-800">
                                <div className="font-semibold mb-1">Something went wrong.</div>
                                <p>{errorMessage}</p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={onBack}
                                className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRetry}
                                className="bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-800 transition-colors"
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                >
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    Back to actions
                </button>
                <div className="text-sm font-semibold text-gray-800">Archive case</div>
                <div className="w-[120px]" />
            </div>

            <div className="flex-1 min-h-0 overflow-auto p-6">
                <div className="max-w-xl mx-auto">
                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            Patient
                        </div>
                        <div className="text-base font-semibold text-gray-900">{patientName}</div>
                    </div>

                    <div className="mt-6 text-sm text-gray-600 space-y-2">
                        <p>Archiving closes this case and removes it from the active queue.</p>
                        <p>
                            Only active cases can be archived. It can be reactivated later - by ODO
                            from the case detail panel, or by the practice from their dashboard.
                        </p>
                    </div>

                    <div className="mt-6">
                        <ReasonField
                            value={reason}
                            onChange={setReason}
                            disabled={isSubmitting}
                            label="Reason for archiving"
                            placeholder="e.g. Practice requested cancellation, duplicate case, patient moved practices."
                            minLength={REQUIRED_REASON_LENGTH}
                            maxLength={MAX_REASON_LENGTH}
                        />
                    </div>

                    <div className="mt-6 flex justify-end items-center gap-3">
                        <button
                            onClick={onBack}
                            disabled={isSubmitting}
                            className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className="flex items-center gap-2 bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting && (
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            )}
                            {isSubmitting ? 'Archiving…' : 'Yes, archive case'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { ArchiveCaseAction };
